<script setup lang="ts">
// Four-column navy footer from the mockups: brand blurb, quick links,
// helpful resources, and the service-area column.
//
// The contact line is an email address, not a phone number, and it is
// labelled as an admin mailbox — someone who wants HVAC work needs the
// form, not an inbox that can't dispatch anyone (see nav.ts).
import {
    LEGAL_LINKS,
    NAV_ITEMS,
    SECOND_OPINION_HREF,
    SITE_EMAIL,
    SITE_LOCATION,
    SITE_REGION,
} from '../nav'
// Same 128px mark as the header (see AppHeader.vue).
import logoMark from '@/assets/askhvac-logo-128.png'

const QUICK = NAV_ITEMS.filter((i) => i.id !== 'home')

const RESOURCES = [
    { label: 'AC Repair Cost Guide', href: '/cost-guides' },
    { label: 'New AC Cost Guide', href: '/cost-guides#new-system' },
    { label: 'Repair or Replace Calculator', href: '/repair-or-replace' },
    { label: 'System Comparisons', href: '/system-types' },
    { label: 'Financing Options', href: '/financing' },
]

const YEAR = 2026
</script>

<template>
    <footer class="app-footer">
        <div class="cols">
            <div class="col brand-col">
                <span class="brand">
                    <img class="mark" :src="logoMark" alt="" width="128" height="128" />
                    <span class="word">
                        <strong>Ask-HVAC<span class="dot">.com</span></strong>
                        <small>Get Answers Before You Call</small>
                    </span>
                </span>
                <p class="blurb">
                    Independent HVAC guidance to help homeowners make the best decisions for their
                    comfort, health, and budget.
                </p>
            </div>

            <div class="col">
                <h3>Quick Links</h3>
                <ul>
                    <li><a href="/">Home</a></li>
                    <li v-for="item in QUICK" :key="item.id">
                        <a :href="item.href">{{ item.label }}</a>
                    </li>
                </ul>
            </div>

            <div class="col">
                <h3>Helpful Resources</h3>
                <ul>
                    <li v-for="r in RESOURCES" :key="r.href">
                        <a :href="r.href">{{ r.label }}</a>
                    </li>
                </ul>
            </div>

            <div class="col">
                <h3>Serving {{ SITE_REGION }}</h3>
                <p class="blurb">
                    Proudly helping homeowners make smarter HVAC decisions.
                </p>
                <p class="contact-line">{{ SITE_LOCATION }}</p>
                <p class="contact-line admin">
                    <a :href="`mailto:${SITE_EMAIL}`">{{ SITE_EMAIL }}</a>
                    <small>Site questions and corrections — not a service line.</small>
                </p>
                <a class="cta" :href="SECOND_OPINION_HREF">Get a Free Second Opinion →</a>
            </div>
        </div>

        <div class="base">
            <p>© {{ YEAR }} Ask-HVAC.com. All rights reserved. Independent HVAC guidance for homeowners.</p>
            <nav class="legal">
                <a v-for="l in LEGAL_LINKS" :key="l.href" :href="l.href">{{ l.label }}</a>
            </nav>
        </div>
    </footer>
</template>

<style scoped lang="scss">
.app-footer {
    background: var(--site-navy-deep);
    color: rgba(255, 255, 255, 0.82);
}

.cols {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-2xl) var(--site-space-lg) var(--site-space-xl);
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 1.2fr;
    gap: var(--site-space-xl);

    @media (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

h3 {
    font-family: var(--site-font-heading);
    font-size: 0.95rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 var(--site-space-md);
}

ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
}

a {
    color: rgba(255, 255, 255, 0.82);
    text-decoration: none;
    font-size: 0.88rem;

    &:hover {
        color: #fff;
    }
}

/* The footer carries ~13 links per page, and on a phone they were 17px
   rows sitting 7px apart — the densest mis-tap hazard on the site. Here
   each becomes its own 44px row. Desktop keeps the tight column. */
@media (max-width: 860px) {
    .col ul {
        gap: 0;

        a {
            display: flex;
            align-items: center;
            min-height: 44px;
            font-size: 0.92rem;
        }
    }

    .legal a {
        display: inline-flex;
        align-items: center;
        min-height: 44px;
        font-size: 0.82rem;
    }
}

.brand {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: var(--site-space-md);

    .mark {
        width: 2.3rem;
        height: 2.3rem;
        /* Slightly darker field than the footer's navy, so a hairline
           keeps the tile from dissolving into the background. */
        border-radius: 7px;
        display: block;
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    }

    .word {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
        color: #fff;
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 1.15rem;
        font-weight: 800;
        letter-spacing: -0.03em;
    }

    .dot {
        color: #6ea8f5;
    }

    small {
        font-size: 0.6rem;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.7);

        @media (max-width: 860px) {
            font-size: 0.72rem;
        }
    }
}

.blurb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.85rem;
    line-height: 1.6;
}

.contact-line {
    margin: 0 0 0.35rem;
    font-size: 0.88rem;

    strong {
        color: #fff;
    }

    &.admin {
        display: flex;
        flex-direction: column;
        gap: 0.1rem;

        a {
            color: #9ec5f8;
            font-weight: 600;

            &:hover {
                color: #fff;
            }
        }

        small {
            font-size: 0.72rem;
            color: rgba(255, 255, 255, 0.55);
            line-height: 1.4;
        }
    }
}

.cta {
    display: inline-block;
    margin-top: var(--site-space-md);
    padding: 0.5rem 0.9rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;

    &:hover {
        background: var(--site-accent-strong);
    }

    @media (max-width: 860px) {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
        font-size: 0.92rem;
    }
}

@media (max-width: 860px) {
    .contact-line.admin a {
        display: inline-flex;
        align-items: center;
        min-height: 44px;
    }
}

.base {
    border-top: 1px solid rgba(255, 255, 255, 0.14);

    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    align-items: center;
    justify-content: space-between;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-md) var(--site-space-lg);

    p {
        margin: 0;
        font-size: 0.76rem;
        color: rgba(255, 255, 255, 0.6);
    }
}

.legal {
    display: flex;
    gap: var(--site-space-md);

    a {
        font-size: 0.76rem;
        color: rgba(255, 255, 255, 0.6);
    }
}
</style>
